import React from 'react';
import { Box, ButtonBase, InputBase, Typography } from '@mui/material';
import {
  DateRange,
  PERIOD_OPTIONS,
  PeriodKey,
  formatRange,
  toDateInput
} from './period';
import { bodyFont, ink, surface } from './tokens';

interface PeriodFilterProps {
  period: PeriodKey;
  onPeriodChange: (period: PeriodKey) => void;
  customStart: string;
  customEnd: string;
  onCustomStartChange: (value: string) => void;
  onCustomEndChange: (value: string) => void;
  /** The resolved range, echoed back so the reader can see what is on screen. */
  range: DateRange;
  error?: string;
}

const dateFieldSx = {
  height: 38,
  px: '10px',
  border: `1px solid ${surface.borderStrong}`,
  borderRadius: '9px',
  backgroundColor: surface.card,
  fontFamily: bodyFont,
  fontSize: 14,
  color: ink.strong,
  '& input': { p: 0 }
};

/**
 * The page's single range control. One segmented control drives every chart
 * and table below it, so two panels can never disagree about what "this
 * period" means.
 */
export const PeriodFilter: React.FC<PeriodFilterProps> = ({
  period,
  onPeriodChange,
  customStart,
  customEnd,
  onCustomStartChange,
  onCustomEndChange,
  range,
  error
}) => {
  const today = toDateInput(new Date());

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap'
        }}
      >
        <Box
          role='group'
          aria-label='Reporting period'
          sx={{
            display: 'inline-flex',
            border: `1px solid ${surface.borderStrong}`,
            borderRadius: '10px',
            backgroundColor: surface.card,
            p: '3px',
            gap: '2px',
            flexWrap: 'wrap'
          }}
        >
          {PERIOD_OPTIONS.map((option) => {
            const active = option.key === period;
            return (
              <ButtonBase
                key={option.key}
                aria-pressed={active}
                onClick={() => onPeriodChange(option.key)}
                sx={{
                  px: '12px',
                  height: 32,
                  borderRadius: '7px',
                  backgroundColor: active ? ink.strong : 'transparent',
                  color: active ? '#fff' : ink.body,
                  fontFamily: bodyFont,
                  fontWeight: 500,
                  fontSize: 14,
                  whiteSpace: 'nowrap',
                  '&:hover': { backgroundColor: active ? ink.strong : surface.grid }
                }}
              >
                {option.label}
              </ButtonBase>
            );
          })}
        </Box>

        <Typography sx={{ fontFamily: bodyFont, fontSize: 14, color: ink.muted }}>
          {formatRange(range)}
        </Typography>
      </Box>

      {period === 'custom' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <Box component='label' sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography sx={{ fontFamily: bodyFont, fontSize: 14, color: ink.muted }}>
              From
            </Typography>
            <InputBase
              type='date'
              value={customStart}
              inputProps={{ max: customEnd || today, 'aria-label': 'Start date' }}
              onChange={(event) => onCustomStartChange(event.target.value)}
              sx={dateFieldSx}
            />
          </Box>
          <Box component='label' sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography sx={{ fontFamily: bodyFont, fontSize: 14, color: ink.muted }}>
              To
            </Typography>
            <InputBase
              type='date'
              value={customEnd}
              inputProps={{ min: customStart, max: today, 'aria-label': 'End date' }}
              onChange={(event) => onCustomEndChange(event.target.value)}
              sx={dateFieldSx}
            />
          </Box>
        </Box>
      )}

      {error && (
        <Typography sx={{ fontFamily: bodyFont, fontSize: 14, color: '#9A3412' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default PeriodFilter;
